import { useEffect, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { $api } from '../../api/queryClient'
import { apiErrorDetail } from '../../lib/apiError'
import { Button } from '../ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { useRefreshApplications } from './shared'

/** An application by hand: a listing and the lead applicant, a new contact. */
export function ApplicationCreateDialog({ org, open, onOpenChange, listingUid }: { org: string; open: boolean; onOpenChange: (o: boolean) => void; listingUid?: string }) {
    const { t } = useTranslation('rentalApplications')
    const navigate = useNavigate()
    const refresh = useRefreshApplications()
    const [listing, setListing] = useState(listingUid ?? '')
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [error, setError] = useState<string | null>(null)
    // One Idempotency-Key per opening: a double submit creates once.
    const [idemKey, setIdemKey] = useState('')
    useEffect(() => {
        if (open) setIdemKey(crypto.randomUUID())
    }, [open])
    const { data: listings } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/listings', { params: { path: { orgCode: org } } }, { enabled: open })
    const create = $api.useMutation('post', '/api/v1/organizations/{orgCode}/rental-applications', {
        onSuccess: (res) => {
            refresh()
            onOpenChange(false)
            navigate({ to: '/$org/rental-applications/$applicationUid', params: { org, applicationUid: res.application.uid } })
        },
        onError: (err) => setError(apiErrorDetail(err, t('create.error'))),
    })
    const live = (listings?.listings ?? []).filter((l) => l.status !== 'withdrawn' && l.status !== 'let')

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="rental-application-create-dialog">
                <DialogHeader>
                    <DialogTitle>{t('create.title')}</DialogTitle>
                    <DialogDescription>{t('create.description')}</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label className="mb-1 block text-sm">{t('create.listing')}</Label>
                        <Select value={listing} onValueChange={setListing}>
                            <SelectTrigger data-testid="rental-application-create-listing"><SelectValue placeholder={t('create.listing_placeholder')} /></SelectTrigger>
                            <SelectContent>
                                {live.map((l) => (
                                    <SelectItem key={l.uid} value={l.uid} data-testid={`rental-application-create-listing-${l.uid}`}>{l.asset_name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="mb-1 block text-sm">{t('create.lead_name')}</Label>
                        <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="rental-application-create-name" />
                    </div>
                    <div>
                        <Label className="mb-1 block text-sm">{t('create.lead_email')}</Label>
                        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="rental-application-create-email" />
                    </div>
                    {error && <p className="text-sm text-destructive" data-testid="rental-application-create-error">{error}</p>}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="rental-application-create-cancel">{t('cancel')}</Button>
                    <Button
                        disabled={!listing || !name.trim() || create.isPending}
                        onClick={() => create.mutate({ params: { path: { orgCode: org }, header: { 'Idempotency-Key': idemKey } }, body: { listing_uid: listing, lead: { name, email: email || undefined } } })}
                        data-testid="rental-application-create-submit"
                    >
                        {t('create.submit')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
